import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import DataTable, { Column } from '@repo/ui/data-table'
import { StarIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getEnrollmentCenters } from '../../../api/centers/get-enrollment-centers'
import { genderOneLetterMapping } from '../../../lib/types/enums/common/gender.enum'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentCenter } from '../../../lib/types/models/enrollment-center/enrollment-center'

type TripCenterEnrollmentsProps = {
  centerId: number
  tripId: number
}

export function TripCenterEnrollments({ centerId, tripId }: TripCenterEnrollmentsProps) {
  const { data } = useQuery({
    queryKey: ['center-trip-enrollments', centerId, tripId],
    queryFn: () => getEnrollmentCenters({ filter: { centerId, mainTripId: tripId } })
  })

  const columns: Column<EnrollmentCenter>[] = [
    {
      accessorKey: 'enrollment.childLastName',
      columnHeaderClassName: 'rounded-tl-lg',
      header: 'Nom',
      cell: ({
        row: {
          original: { enrollment }
        }
      }) => valueRender(enrollment?.childLastName, (l) => l.toUpperCase())
    },
    {
      accessorKey: 'enrollment.childFirstName',
      header: 'Prénom',
      cell: ({
        row: {
          original: { enrollment }
        }
      }) => valueRender(enrollment?.childFirstName)
    },
    {
      accessorKey: 'enrollment.blockedFriends',
      header: 'Ami(s)',
      cell: ({
        row: {
          original: { enrollment }
        }
      }) =>
        enrollment?.blockedFriends?.length
          ? valueRender(enrollment?.blockedFriends, (f) => f.map((f) => f.firstName).join(', '))
          : '-'
    },
    {
      accessorKey: 'enrollment.childBirthDate',
      header: 'Âge',
      cell: ({
        row: {
          original: { enrollment }
        }
      }) => EnrollmentProperties.getAgeAtDeparture(enrollment)?.toString() ?? '-'
    },
    {
      accessorKey: 'enrollment.childGender',
      header: 'Sexe',
      cell: ({
        row: {
          original: { enrollment }
        }
      }) => genderOneLetterMapping[enrollment?.childGender ?? 'NONE'].label
    },
    {
      accessorKey: 'enrollment.isAA',
      header: 'AA',
      columnHeaderClassName: 'rounded-tr-lg',
      cell: ({ row }) =>
        row.original.enrollment?.isAA && (
          <div className={'bg-yellow-light rounded-full p-1 w-fit'}>
            <StarIcon filled color={tailwindColors.yellow.DEFAULT} />
          </div>
        )
    }
  ]

  return (
    <div className={'flex flex-col gap-2'}>
      <Typography variant={'tag'} color={tailwindColors.primary.DEFAULT}>
        Jeunes
      </Typography>
      <DataTable<EnrollmentCenter>
        bodyVariant={'white'}
        columns={columns}
        data={data?.data ?? []}
        rowCount={data?.total ?? 0}
        paginated={false}
      />
    </div>
  )
}
